import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Drawer, Form, Typography, Spin, Space, Button, Tooltip, App } from 'antd';
import { getSponsor, getConferenceBenefitList, deliverBenefit } from '@services/Conference';
import { pageFormItemLayout } from '@utils/property';
import { BENEFIT_CATEGORIES } from '../../constants';
import { computeRealizedCountFromStates, initBenefitStates, stringifyRealizationFiles } from './realizationTransform';
import RealizationBenefitRow from './RealizationBenefitRow';
import AssociateExpertModal from './AssociateExpertModal';
import { EMPTY_PLACEHOLDER } from '../../constants';
import OperationLogDrawerTrigger from '@/components/common/OperationLogDrawerTrigger';
import { OperationLogDrawerHostProvider } from '@/components/common/operationLogDrawerHostContext';
import { logOrgBenefitRealize } from '@/utils/conferenceLogger';
import { buildRealizationLogSnapshot } from '@/utils/conferenceOpLogSnapshot';
import './index.less';

const { Text } = Typography;

/**
 * 权益兑现主弹窗
 * @param {Object} props
 * @param {boolean} props.open - 是否打开
 * @param {number} props.sponsorId - 机构 ID
 * @param {Function} props.onClose - 关闭回调
 * @param {Function} props.onSuccess - 保存成功回调（刷新列表）
 */
export default function BenefitRealizationModal({ open, sponsorId, onClose, onSuccess }) {
    const { modal, message } = App.useApp();
    const params = useParams();
    const conferenceId = params?.id || 0;

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [sponsor, setSponsor] = useState(null);
    const [benefitList, setBenefitList] = useState([]);
    const [benefitStates, setBenefitStates] = useState({});
    const [uploadProgress, setUploadProgress] = useState({ completed: 0, total: 0 });

    const [initialSnapshot, setInitialSnapshot] = useState(null);

    // 关联专家子弹窗状态
    const [expertModalOpen, setExpertModalOpen] = useState(false);
    const [currentBenefit, setCurrentBenefit] = useState(null);

    /**
     * 加载数据
     */
    useEffect(() => {
        if (!open || !sponsorId) {
            return;
        }

        const fetchData = async () => {
            setLoading(true);
            try {
                // 获取机构详情与 benefitList
                const sponsorRes = await getSponsor(sponsorId);
                if (sponsorRes.status_code !== 200 || !sponsorRes.data) {
                    message.error('数据加载失败');
                    return;
                }

                const sponsorData = sponsorRes.data;
                setSponsor(sponsorData);

                const rawBenefitList = sponsorData.benefitList || [];

                // 获取权益池元数据（补充 type, isBoothBenefit, benefitName）
                if (sponsorData.conferenceId) {
                    const benefitPoolRes = await getConferenceBenefitList({ conferenceId: sponsorData.conferenceId });
                    if (benefitPoolRes.status_code === 200 && benefitPoolRes.data) {
                        const benefitPool = benefitPoolRes.data;
                        // 合并权益数据（将池中的元数据补充到 benefitList）
                        const enrichedBenefitList = rawBenefitList.map(b => {
                            const poolItem = benefitPool.find(p => p.id === b.conferenceBenefitId);
                            return {
                                ...b,
                                type: poolItem?.type,
                                isBoothBenefit: poolItem?.isBoothBenefit,
                                benefitName: poolItem?.benefitName || b.benefitName,
                            };
                        });
                        setBenefitList(enrichedBenefitList);

                        const states = initBenefitStates(enrichedBenefitList);
                        setBenefitStates(states);
                        setInitialSnapshot(JSON.stringify(states));
                    }
                } else {
                    setBenefitList(rawBenefitList);
                    const states = initBenefitStates(rawBenefitList);
                    setBenefitStates(states);
                    setInitialSnapshot(JSON.stringify(states));
                }
            } catch (error) {
                console.error('Failed to load data:', error);
                message.error('数据加载失败');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [open, sponsorId, message]);

    /**
     * 重置状态
     */
    const resetState = () => {
        setSponsor(null);
        setBenefitList([]);
        setBenefitStates({});
        setUploadProgress({ completed: 0, total: 0 });
        setInitialSnapshot(null);
        setExpertModalOpen(false);
        setCurrentBenefit(null);
    };

    /**
     * 关闭弹窗
     */
    const closeModal = () => {
        resetState();
        onClose();
    };

    /**
     * 取消按钮：上传中优先提示；否则脏检查
     */
    const handleCancel = () => {
        if (uploadProgress.total > uploadProgress.completed) {
            modal.confirm({
                title: '提示',
                content: '文件上传中，确认结束上传并关闭？',
                okText: '确认',
                cancelText: '取消',
                centered: true,
                onOk: closeModal,
            });
            return;
        }

        const currentSnapshot = JSON.stringify(benefitStates);
        if (initialSnapshot !== currentSnapshot) {
            modal.confirm({
                title: '提示',
                content: '发现有数据变动，确认不保存并关闭？',
                okText: '确认',
                cancelText: '取消',
                centered: true,
                onOk: closeModal,
            });
        } else {
            closeModal();
        }
    };

    /**
     * 子组件上传进度变化：start 计入总数，finish 计入已完成，fail 失败回退总数，remove 回退本次上传成功计数
     */
    const handleUploadProgressChange = phase => {
        setUploadProgress(prev => {
            if (phase === 'start') {
                return { ...prev, total: prev.total + 1 };
            }
            if (phase === 'finish') {
                return { ...prev, completed: prev.completed + 1 };
            }
            if (phase === 'fail') {
                return { ...prev, total: Math.max(0, prev.total - 1) };
            }
            if (phase === 'remove') {
                return {
                    completed: Math.max(0, prev.completed - 1),
                    total: Math.max(0, prev.total - 1),
                };
            }
            return prev;
        });
    };

    const isUploading = uploadProgress.total > uploadProgress.completed;
    const isUploadComplete = !isUploading && uploadProgress.total > 0 && uploadProgress.completed === uploadProgress.total;

    /**
     * 渲染文件上传进度图标：上传中转圈，完成后绿色对勾，其余默认对勾
     */
    const renderUploadProgressIcon = () => {
        if (isUploading) {
            return <span className="benefit-upload-spinner" />;
        }
        return <i className={`bi bi-check-circle-fill benefit-upload-status-icon${isUploadComplete ? ' is-complete' : ''}`} />;
    };

    /**
     * 保存按钮
     */
    const handleSave = async () => {
        // 检查是否有图片正在上传
        if (isUploading) {
            message.warning('文件正在上传中，请全部上传完成后再保存');
            return;
        }

        // 筛选修改过的权益（接口 benefitId 取权益记录 benefitId，非 state key 的 id）
        const changedBenefits = Object.values(benefitStates)
            .filter(state => state.changed)
            .map(state => ({
                benefitId: state.benefitId,
                conferenceId,
                sponsorId: sponsor.id,
                realizationFiles: stringifyRealizationFiles({
                    images: state.images || [],
                    experts: state.experts || [],
                }),
            }));

        if (changedBenefits.length === 0) {
            message.info('无变更，无需保存');
            closeModal();
            return;
        }

        setSaving(true);
        const changeSnapshot = buildRealizationLogSnapshot(initialSnapshot, changedBenefits);
        try {
            const res = await deliverBenefit(changedBenefits);
            if (res.status_code === 200) {
                try {
                    await logOrgBenefitRealize({
                        sponsorId,
                        orgName: sponsor?.name,
                        changeSnapshot,
                        isSuccess: true,
                    });
                } catch (logError) {
                    console.error('操作日志上报失败', logError);
                }
                message.success('保存成功');
                onSuccess();
                closeModal();
            } else {
                try {
                    await logOrgBenefitRealize({
                        sponsorId,
                        orgName: sponsor?.name,
                        changeSnapshot,
                        isSuccess: false,
                        errorMessage: res.message || '保存失败',
                    });
                } catch (logError) {
                    console.error('操作日志上报失败', logError);
                }
                message.error(res.message || '保存失败');
            }
        } catch (error) {
            console.error('权益兑现保存失败', error);
            try {
                await logOrgBenefitRealize({
                    sponsorId,
                    orgName: sponsor?.name,
                    changeSnapshot,
                    isSuccess: false,
                    errorMessage: error?.message || '保存失败',
                });
            } catch (logError) {
                console.error('操作日志上报失败', logError);
            }
            message.error('保存失败');
        } finally {
            setSaving(false);
        }
    };

    /**
     * 单条权益状态变更，支持对象或函数式更新
     * 函数式更新可避免并发上传时闭包捕获旧 state 的问题
     */
    const handleBenefitChange = (benefitId, newStateOrUpdater) => {
        setBenefitStates(prev => {
            const currentState = prev[benefitId] || {};
            const nextState = typeof newStateOrUpdater === 'function' ? newStateOrUpdater(currentState) : newStateOrUpdater;
            return { ...prev, [benefitId]: nextState };
        });
    };

    /**
     * 打开关联专家子弹窗
     */
    const handleAssociateExpert = benefit => {
        setCurrentBenefit(benefit);
        setExpertModalOpen(true);
    };

    /**
     * 关联专家确认：保存专家 id 与姓名，便于回显
     */
    const handleExpertConfirm = (_expertIds, experts) => {
        if (!currentBenefit) return;

        const benefitId = currentBenefit.id;
        const normalizedExperts = (experts || []).map(expert => ({
            id: expert.id,
            name: expert.memUsername || '',
        }));

        setBenefitStates(prev => ({
            ...prev,
            [benefitId]: {
                ...(prev[benefitId] || {}),
                experts: normalizedExperts,
                changed: true,
            },
        }));

        setExpertModalOpen(false);
        setCurrentBenefit(null);
    };

    /**
     * 关联专家取消
     */
    const handleExpertCancel = () => {
        setExpertModalOpen(false);
        setCurrentBenefit(null);
    };

    /**
     * 兑现进度统计（基于当前 benefitStates 实时计算，有图片即已兑现）
     */
    const realizedCount = computeRealizedCountFromStates(benefitStates);
    const totalCount = benefitList.length;

    /**
     * 按分类分组展示
     */
    const renderBenefitsByCategory = () => {
        return BENEFIT_CATEGORIES.map(category => {
            const categoryBenefits = benefitList.filter(b => b.type === category.id);
            if (categoryBenefits.length === 0) return null;

            // 论坛分类：按 hallsId 分组展示
            if (category.id === 4) {
                const hallGroups = {};
                for (const b of categoryBenefits) {
                    const key = b.hallsId || 'unknown';
                    if (!hallGroups[key]) {
                        hallGroups[key] = { hallName: b.hallName || '', benefits: [] };
                    }
                    hallGroups[key].benefits.push(b);
                }

                return (
                    <div key={category.id} className="benefit-category-section">
                        <div className="benefit-category-header">
                            <span className="benefit-category-title">权益分类：{category.name}</span>
                        </div>
                        {Object.entries(hallGroups).map(([hallKey, group]) => (
                            <div key={hallKey}>
                                {group.hallName && <div className="benefit-forum-title">{`论坛：${group.hallName}`}</div>}
                                <div className="benefit-category-list">
                                    {group.benefits.map(benefit => (
                                        <RealizationBenefitRow key={benefit.id} benefit={benefit} state={benefitStates[benefit.id] || {}} onChange={newState => handleBenefitChange(benefit.id, newState)} onAssociateExpert={handleAssociateExpert} onUploadProgressChange={handleUploadProgressChange} />
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                );
            }

            return (
                <div key={category.id} className="benefit-category-section">
                    <div className="benefit-category-header">
                        <span className="benefit-category-title">权益分类：{category.name}</span>
                    </div>
                    <div className="benefit-category-list">
                        {categoryBenefits.map(benefit => (
                            <RealizationBenefitRow key={benefit.id} benefit={benefit} state={benefitStates[benefit.id] || {}} onChange={newState => handleBenefitChange(benefit.id, newState)} onAssociateExpert={handleAssociateExpert} onUploadProgressChange={handleUploadProgressChange} />
                        ))}
                    </div>
                </div>
            );
        });
    };

    return (
        <OperationLogDrawerHostProvider>
            <Drawer
                title={
                    <div className="benefit-realization-drawer-title">
                        <div className="benefit-realization-drawer-title__left">
                            <button type="button" className="drawer-back" onClick={handleCancel} aria-label="返回上一界面">
                                <i className="bi bi-chevron-left" aria-hidden="true" />
                            </button>
                            <span>权益兑现</span>
                        </div>
                        <Text type="warning">
                            {renderUploadProgressIcon()}
                            {`文件上传进度 ${uploadProgress.completed}/${uploadProgress.total}`}
                        </Text>
                    </div>
                }
                open={open}
                closable={false}
                size="100%"
                placement="bottom"
                push={false}
                layout="horizontal"
                mask={{ closable: false }}
                className="applog-record-drawer"
                footer={
                    <div style={{ textAlign: 'right' }}>
                        <Space>
                            <Button onClick={handleCancel}>取消</Button>
                            <Tooltip title={isUploading ? '请等待上传完成' : ''}>
                                <span>
                                    <Button type="primary" loading={saving} disabled={isUploading} onClick={handleSave}>
                                        保存
                                    </Button>
                                </span>
                            </Tooltip>
                        </Space>
                    </div>
                }
            >
                <Spin spinning={loading}>
                    <OperationLogDrawerTrigger entityId={sponsorId} entityType="conference_sponsor" module="conference" />
                    <div className="conference-form benefit-realization-form">
                        {sponsor && (
                            <Form {...pageFormItemLayout} layout="horizontal" autoComplete="off">
                                <div className="benefit-realization-summary">
                                    <Form.Item label="机构名称：" colon={false}>
                                        <span>{sponsor.name || EMPTY_PLACEHOLDER}</span>
                                    </Form.Item>
                                    <Form.Item label="名义：" colon={false}>
                                        <span>{sponsor.title || EMPTY_PLACEHOLDER}</span>
                                    </Form.Item>
                                    <Form.Item label="金额（元）：" colon={false}>
                                        <span>{sponsor.contractAmount ? sponsor.contractAmount : EMPTY_PLACEHOLDER}</span>
                                    </Form.Item>
                                    <Form.Item label="合同权益：" colon={false}>
                                        <span>{`共${totalCount}项，已兑现${realizedCount}项`}</span>
                                    </Form.Item>
                                </div>
                                <Form.Item label=" " colon={false}>
                                    {renderBenefitsByCategory()}
                                </Form.Item>
                            </Form>
                        )}
                    </div>
                </Spin>
            </Drawer>

            {/* 关联专家子弹窗 */}
            <AssociateExpertModal
                open={expertModalOpen}
                hallId={currentBenefit?.hallsId}
                hallName={currentBenefit?.hallName}
                dates={currentBenefit?.dates}
                selectedExpertIds={currentBenefit ? (benefitStates[currentBenefit.id]?.experts || []).map(expert => expert.id) : []}
                onConfirm={handleExpertConfirm}
                onCancel={handleExpertCancel}
            />
        </OperationLogDrawerHostProvider>
    );
}
